import React, { FC, ReactElement, ReactNode, useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { Spinner } from '@vwfs-bronson/bronson-react';
import { useAuth, hasAuthParams } from 'react-oidc-context';

export type AuthenticationComponentFactoryOptions = {
    onMissingAuthentication: ReactNode;
    onErrorAuthentication?: ReactNode;
};

export function createAuthenticationComponent({ onMissingAuthentication }: AuthenticationComponentFactoryOptions): FC {
    const Authentication: FC = ({ children }): ReactElement => {
        const { keycloak, initialized } = useKeycloak();

        if (!initialized) {
            return <Spinner center />;
        } else if (keycloak.authenticated && keycloak.token && keycloak.tokenParsed) {
            return <>{children}</>;
        } else {
            return <>{onMissingAuthentication}</>;
        }
    };
    return Authentication;
}

export function createOidcAuthenticationComponent({
    onMissingAuthentication,
    onErrorAuthentication,
}: AuthenticationComponentFactoryOptions): FC {
    const Authentication: FC = ({ children }): ReactElement => {
        const auth = useAuth();
        const [hasTriedSignin, setHasTriedSignin] = useState(false);

        useEffect(() => {
            if (
                !hasAuthParams() &&
                !auth.isAuthenticated &&
                !auth.activeNavigator &&
                !auth.isLoading &&
                !hasTriedSignin
            ) {
                auth.signinRedirect({ prompt: 'none' }); // Enables silent sso
                setHasTriedSignin(true);
            }
        }, [auth.isAuthenticated, auth.activeNavigator, auth.isLoading, hasTriedSignin]);

        if (auth.isLoading) {
            return <Spinner center />;
        }

        if (auth.error) {
            return <>{onErrorAuthentication || onMissingAuthentication}</>;
        }

        if (auth.isAuthenticated && auth.user) {
            return <>{children}</>;
        }

        return <>{onMissingAuthentication}</>;
    };

    return Authentication;
}
